/**
 * Timelines that travel with their project (plan 3.1, 2026-10-01). PURE - no
 * signals, no storage, no network, so the rules can be tested on their own
 * (`smokes/timeline_library_sync_rules.ts`).
 *
 * WHY. Timelines lived in one library in each browser's localStorage, with no
 * path between devices, so a timeline built on the desktop did not exist on
 * the iPad and a Launcher there said "Pick a timeline in the Inspector". The
 * operator edits on the desktop and performs from the iPad.
 *
 * HOW, and why this shape (DEC-218's, applied to timelines):
 *   - Each timeline belongs to ONE project (`Timeline.projectId`) and rides
 *     INSIDE that project's `project-<id>` sync document, beside `project`.
 *     Not a `timelines` document of its own: OPBridge replicates only
 *     `project-*` documents between machines, so a separate document would
 *     never reach an iPad whose primary is the other machine. Not inside
 *     `project` either: the project merges whole, so an iPad layout edit and
 *     a desktop timeline edit would overwrite each other.
 *   - A project's document also carries any timeline its items point at
 *     (a Launcher's `timelineId`), so a Launcher never arrives pointing at
 *     nothing, whoever owns the timeline.
 *   - The unit of merge is ONE timeline, decided by its own `modified`.
 *     Ties go to the incoming copy, for convergence (two devices each keeping
 *     their own copy of a tie would re-push it forever).
 *   - ABSENCE IS NOT DELETION. A document without a timeline means "that
 *     writer never had it", not "delete it" - an older build, or OPBridge's
 *     own project writer, may drop fields it does not know. Deletion is an
 *     explicit record (`TimelineTombstone`) that travels in every project
 *     document, so any device that holds any project learns of it.
 *   - AN EDIT AFTER THE DELETE WINS (same rule as `projectMerge.ts`): losing
 *     deliberate work to a stale delete is the worse failure.
 */

import type { Timeline } from '@opbuttons/schema';
import { TOMBSTONE_TTL_MS } from '../layout/projectMerge.ts';

/** A record that a timeline was deleted, and when (epoch ms). */
export interface TimelineTombstone {
  id: string;
  deletedAt: number;
}

/** A timeline's sync stamp in epoch ms: `modified`, else `created`, else 0. */
export function modifiedMs(t: Timeline): number {
  const m = Date.parse(t.modified);
  if (Number.isFinite(m)) return m;
  const c = Date.parse(t.created);
  return Number.isFinite(c) ? c : 0;
}

/** JSON with every object's keys in sorted order. OPBridge stores documents key-sorted (docs.rs, no preserve_order),
 *  so the same timeline comes back with its keys in another order; compared raw, it read as changed, was "adopted",
 *  wiped undo and said "1 timeline updated" whenever another device wrote the project (2026-10-16, the reliability
 *  audit). Every "is this the same timeline" question here asks it in this form. */
export function canonicalJson(v: unknown): string {
  return JSON.stringify(v, (_k, x: unknown) => (x && typeof x === 'object' && !Array.isArray(x)
    ? Object.fromEntries(Object.keys(x as Record<string, unknown>).sort().map((k) => [k, (x as Record<string, unknown>)[k]]))
    : x));
}

function withoutStamp(t: Timeline): string {
  const { modified: _m, ...rest } = t;
  return canonicalJson(rest);
}

/**
 * Stamp `modified = nowIso` on every timeline of `next` that is new or whose
 * content differs from its `prev` copy; an unchanged one keeps its stamp.
 *
 * Runs on EVERY local write, undo and redo included: an undo that restored an
 * old stamp would make the restored version look OLDER than the copy already
 * synced, and the next sync would silently redo the edit.
 */
export function stampChangedTimelines(prev: readonly Timeline[], next: readonly Timeline[], nowIso: string): Timeline[] {
  const prevById = new Map(prev.map((t) => [t.id, t]));
  return next.map((t) => {
    const p = prevById.get(t.id);
    if (p === t) return t;
    if (p && withoutStamp(p) === withoutStamp(t)) {
      return p.modified === t.modified ? t : { ...t, modified: p.modified };
    }
    return t.modified === nowIso ? t : { ...t, modified: nowIso };
  });
}

/** Latest deletion per id, newest wins. */
function deathsOf(...lists: readonly (readonly TimelineTombstone[])[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const list of lists) {
    for (const t of list) {
      if (!t || typeof t.id !== 'string' || typeof t.deletedAt !== 'number') continue;
      const prev = out.get(t.id);
      if (prev === undefined || t.deletedAt > prev) out.set(t.id, t.deletedAt);
    }
  }
  return out;
}

export interface TimelineMergeResult {
  /** The new library list, or `null` when it is unchanged. */
  timelines: Timeline[] | null;
  /** The deletion records to keep: resurrected and aged-out ones dropped. */
  tombstones: TimelineTombstone[];
  /** Timelines that came from the other side: adopted new, or replaced. */
  adopted: number;
  /** Timelines removed here because a deletion record says so. */
  removed: number;
}

/**
 * Merge timelines and deletion records that arrived from other devices into
 * this device's library. Local order is kept; new timelines are appended.
 */
export function mergeTimelines(
  local: readonly Timeline[],
  localTombstones: readonly TimelineTombstone[],
  incoming: readonly Timeline[],
  incomingTombstones: readonly TimelineTombstone[],
  now: number,
): TimelineMergeResult {
  const best = new Map<string, Timeline>();
  for (const t of incoming) {
    if (!t || typeof t.id !== 'string' || !Array.isArray(t.lanes) || !Array.isArray(t.sections)) continue;
    const cur = best.get(t.id);
    if (!cur || modifiedMs(t) >= modifiedMs(cur)) best.set(t.id, t);
  }
  const deaths = deathsOf(localTombstones, incomingTombstones);

  let adopted = 0;
  let removed = 0;
  const out: Timeline[] = [];
  const localIds = new Set<string>();
  for (const l of local) {
    localIds.add(l.id);
    const inc = best.get(l.id);
    let winner = l;
    let fromIncoming = false;
    if (inc && canonicalJson(inc) !== canonicalJson(l) && modifiedMs(inc) >= modifiedMs(l)) {
      winner = inc;
      fromIncoming = true;
    }
    const deletedAt = deaths.get(l.id);
    if (deletedAt !== undefined && modifiedMs(winner) <= deletedAt) { removed++; continue; }
    if (fromIncoming) adopted++;
    out.push(winner);
  }
  for (const [id, inc] of best) {
    if (localIds.has(id)) continue;
    const deletedAt = deaths.get(id);
    if (deletedAt !== undefined && modifiedMs(inc) <= deletedAt) continue;
    out.push(inc);
    adopted++;
  }

  const surviving = new Set(out.map((t) => t.id));
  const tombstones: TimelineTombstone[] = [];
  for (const [id, deletedAt] of deaths) {
    if (surviving.has(id)) continue;
    if (now - deletedAt > TOMBSTONE_TTL_MS) continue;
    tombstones.push({ id, deletedAt });
  }
  tombstones.sort((a, b) => a.deletedAt - b.deletedAt || (a.id < b.id ? -1 : 1));

  return { timelines: adopted > 0 || removed > 0 ? out : null, tombstones, adopted, removed };
}

/** Every `timelineId` referenced anywhere in a project's items, first-seen
 *  order. Walks generically, like `libraryMerge.ts::referencedLibraryIds`, so
 *  a reference on any item kind counts, not only the Launcher's. */
export function referencedTimelineIds(pages: readonly { items?: unknown }[] | undefined): string[] {
  const ids = new Set<string>();
  const walk = (v: unknown, depth: number): void => {
    if (depth > 8 || v === null || typeof v !== 'object') return;
    if (Array.isArray(v)) { for (const x of v) walk(x, depth + 1); return; }
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
      if (k === 'timelineId' && typeof x === 'string' && x) ids.add(x);
      else walk(x, depth + 1);
    }
  };
  for (const page of pages ?? []) walk(page.items, 0);
  return [...ids];
}

/** The timelines a project's sync document carries: the ones it owns, then
 *  any other timeline its items point at. */
export function timelinesForProject(
  projectId: string,
  pages: readonly { items?: unknown }[] | undefined,
  library: readonly Timeline[],
): Timeline[] {
  const out = library.filter((t) => t.projectId === projectId);
  const have = new Set(out.map((t) => t.id));
  const byId = new Map(library.map((t) => [t.id, t]));
  for (const id of referencedTimelineIds(pages)) {
    if (have.has(id)) continue;
    const t = byId.get(id);
    if (t) { out.push(t); have.add(id); }
  }
  return out;
}

/**
 * Give an owner to every timeline that has none, or whose owner is a project
 * this device no longer holds: the first project whose items point at it,
 * else the active project. Returns the new list, or `null` when nothing
 * changed (including when there is no project to give it to yet).
 *
 * This is also the one-time import the plan asks for: a device's existing
 * library joins its projects the first time the new build runs, and nothing
 * is dropped. A timeline whose project was deleted stays, in the active
 * project, rather than vanishing from every list.
 */
export function claimUnownedTimelines(
  library: readonly Timeline[],
  projects: readonly { id: string; pages?: readonly { items?: unknown }[] }[],
  activeProjectId: string | null,
): Timeline[] | null {
  if (projects.length === 0) return null;
  const projectIds = new Set(projects.map((p) => p.id));
  const fallback = activeProjectId && projectIds.has(activeProjectId) ? activeProjectId : projects[0]!.id;
  let referencedBy: Map<string, string> | null = null;
  let changed = false;
  const out = library.map((t) => {
    if (t.projectId && projectIds.has(t.projectId)) return t;
    if (!referencedBy) {
      referencedBy = new Map();
      for (const p of projects) {
        for (const id of referencedTimelineIds(p.pages)) {
          if (!referencedBy.has(id)) referencedBy.set(id, p.id);
        }
      }
    }
    changed = true;
    return { ...t, projectId: referencedBy.get(t.id) ?? fallback };
  });
  return changed ? out : null;
}

/** Whether a timeline shows in the active project's lists. With no projects
 *  at all (a fresh device before its first one), everything shows. */
export function timelineInProject(t: Timeline, activeProjectId: string | null): boolean {
  return activeProjectId === null || t.projectId === undefined || t.projectId === activeProjectId;
}
